"use client";

import { useEffect, useState, useCallback, useRef } from "react";

interface UseAutosaveFormOptions<T> {
  formKey: string;
  initialValues: T;
  debounceMs?: number;
}

export function useAutosaveForm<T extends Record<string, any>>({
  formKey,
  initialValues,
  debounceMs = 600,
}: UseAutosaveFormOptions<T>) {
  const storageKey = `johnson_form_draft_${formKey}`;
  const [formData, setFormData] = useState<T>(initialValues);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasDraft, setHasDraft] = useState<boolean>(false);
  const isInitialMount = useRef(true);

  // Check and restore draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.data && typeof parsed.data === "object") {
          setFormData((prev) => ({ ...prev, ...parsed.data }));
          setLastSaved(new Date(parsed.timestamp));
          setHasDraft(true);
        }
      }
    } catch (e) {
      console.warn("Autosave load note:", e);
    }
  }, [storageKey]);

  // Save changes with debounce
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      try {
        const hasContent = Object.values(formData).some((v) => Boolean(v));
        if (hasContent) {
          const now = new Date();
          localStorage.setItem(
            storageKey,
            JSON.stringify({
              data: formData,
              timestamp: now.toISOString(),
            })
          );
          setLastSaved(now);
          setHasDraft(true);
        }
      } catch (e) {
        console.warn("Autosave save note:", e);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [formData, storageKey, debounceMs]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setFormData(initialValues);
      setLastSaved(null);
      setHasDraft(false);
    } catch (e) {
      console.warn("Autosave clear note:", e);
    }
  }, [storageKey, initialValues]);

  const markSubmitted = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setLastSaved(null);
      setHasDraft(false);
    } catch (e) {
      console.warn("Autosave submit note:", e);
    }
  }, [storageKey]);

  return {
    formData,
    setFormData,
    lastSaved,
    hasDraft,
    clearDraft,
    markSubmitted,
  };
}
